import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { theme } from '../styles/GlobalStyle';

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(13, 71, 161, 0.6);
  backdrop-filter: blur(5px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  animation: ${fadeIn} 0.3s ease;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 24px;
  padding: 2rem;
  max-width: 500px;
  width: 90%;
  border: 2px solid ${theme.borderLight};
  box-shadow: 0 15px 50px rgba(13, 71, 161, 0.3);
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 6px;
    background: linear-gradient(90deg, ${theme.primary}, ${theme.secondary});
    border-top-left-radius: 24px;
    border-top-right-radius: 24px;
  }
`;

const ModalTitle = styled.h2`
  color: ${theme.primary};
  margin-bottom: 1.5rem;
  font-family: 'Fredoka', sans-serif;
  position: relative;
  padding-bottom: 0.75rem;
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 50px;
    height: 3px;
    background: ${theme.secondary};
    border-radius: 3px;
  }
`;

const Tabs = styled.div`
  display: flex;
  margin-bottom: 1.5rem;
  border-bottom: 2px solid ${theme.borderLight};
`;

const Tab = styled.button`
  flex: 1;
  background: none;
  border: none;
  padding: 0.75rem 1rem;
  font-family: 'Fredoka', sans-serif;
  font-size: 1rem;
  font-weight: 600;
  color: ${props => props.active ? theme.primary : theme.textSecondary};
  position: relative;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -2px;
    left: 0;
    width: 100%;
    height: 3px;
    background: ${props => props.active ? theme.primary : 'transparent'};
    border-radius: 3px 3px 0 0;
    transition: all 0.3s ease;
  }
  
  &:hover {
    color: ${theme.primary};
  }
`;

const InputGroup = styled.div`
  margin-bottom: 1.5rem;
  
  label {
    display: block;
    margin-bottom: 0.5rem;
    font-weight: 600;
    font-size: 0.95rem;
    color: ${theme.textSecondary};
  }
`;

const Input = styled.input`
  width: 100%;
  background: rgba(255, 255, 255, 0.9);
  border: 2px solid ${theme.borderLight};
  color: ${theme.text};
  border-radius: 12px;
  padding: 0.75rem 1rem;
  margin-bottom: 0;
  box-shadow: inset 0 2px 5px rgba(13, 71, 161, 0.05);
  transition: all 0.3s ease;
  font-family: 'Quicksand', sans-serif;
  
  &:focus {
    outline: none;
    border-color: ${theme.primary};
    box-shadow: 0 0 0 3px rgba(13, 71, 161, 0.1),
                inset 0 2px 5px rgba(13, 71, 161, 0.05);
  }
  
  &::placeholder {
    color: ${theme.textMuted};
    opacity: 0.7;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  background: rgba(255, 255, 255, 0.9);
  border: 2px solid ${theme.borderLight};
  color: ${theme.text};
  border-radius: 12px;
  padding: 0.75rem 1rem;
  margin-bottom: 0;
  box-shadow: inset 0 2px 5px rgba(13, 71, 161, 0.05);
  transition: all 0.3s ease;
  font-family: 'Quicksand', sans-serif;
  min-height: 100px;
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: ${theme.primary};
    box-shadow: 0 0 0 3px rgba(13, 71, 161, 0.1),
                inset 0 2px 5px rgba(13, 71, 161, 0.05);
  }
  
  &::placeholder {
    color: ${theme.textMuted};
    opacity: 0.7;
  }
`;

const InfoText = styled.p`
  color: ${theme.textSecondary};
  font-size: 0.85rem;
  margin-top: 0.5rem;
  background-color: rgba(13, 71, 161, 0.05);
  padding: 0.75rem;
  border-radius: 8px;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  justify-content: flex-end;
  margin-top: 1.5rem;
`;

const Button = styled.button`
  background: ${props => props.primary ? 
    'linear-gradient(90deg, #0D47A1, #00C853)' : 
    'rgba(255, 255, 255, 0.9)'};
  color: ${props => props.primary ? '#FFFFFF' : theme.text};
  border: ${props => props.primary ? 'none' : `2px solid ${theme.borderLight}`};
  padding: ${props => props.large ? '1rem 1.5rem' : '0.6rem 1rem'};
  border-radius: 50px;
  font-weight: 600;
  font-size: ${props => props.large ? '1.1rem' : '0.9rem'};
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: 'Fredoka', sans-serif;
  position: relative;
  overflow: hidden;
  box-shadow: ${props => props.primary ? '0 5px 15px rgba(13, 71, 161, 0.25)' : 'none'};
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: ${props => props.primary ? 
      '0 8px 25px rgba(13, 71, 161, 0.35)' : 
      '0 5px 15px rgba(13, 71, 161, 0.1)'};
    border-color: ${props => props.primary ? 'none' : theme.border};
  }
`;

const ErrorMessage = styled.div`
  color: ${theme.error};
  margin-top: 0.5rem;
  padding: 0.5rem;
  font-size: 0.9rem;
  background-color: rgba(211, 47, 47, 0.1);
  border-radius: 8px;
  border: 1px solid ${theme.error};
`;

export const WalletModal = ({ isOpen, onClose, onSubmit }) => {
  const [activeTab, setActiveTab] = useState('seed'); // 'seed' or 'mnemonic'
  const [seed, setSeed] = useState('');
  const [mnemonic, setMnemonic] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = (e) => {
    e.stopPropagation();
    
    if (activeTab === 'seed') {
      if (!seed.trim()) {
        setError('Please enter a seed phrase');
        return;
      }
      
      // For seed phrases, submit directly
      onSubmit({ type: 'seed', value: seed.trim() });
    } else {
      if (!mnemonic.trim()) {
        setError('Please enter your mnemonic words');
        return;
      }
      
      // For mnemonic, submit with type
      onSubmit({ type: 'mnemonic', value: mnemonic.trim() });
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setError('');
  };

  if (!isOpen) return null;

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={e => e.stopPropagation()}>
        <ModalTitle>Import Wallet</ModalTitle>
        
        <Tabs>
          <Tab 
            active={activeTab === 'seed'} 
            onClick={() => handleTabChange('seed')}
          >
            Seed Phrase
          </Tab>
          <Tab 
            active={activeTab === 'mnemonic'} 
            onClick={() => handleTabChange('mnemonic')}
          >
            Mnemonic Words
          </Tab>
        </Tabs>
        
        {activeTab === 'seed' ? (
          <InputGroup>
            <label>Enter your wallet seed phrase</label>
            <Input
              type="text"
              value={seed}
              onChange={(e) => {
                setSeed(e.target.value);
                setError('');
              }}
              placeholder="sXXXXXXXXXXXXXXXXXXXXXXXXXXXXXX"
              onClick={e => e.stopPropagation()}
            />
            <InfoText>
              The seed usually starts with "s" followed by a string of characters.
            </InfoText>
          </InputGroup>
        ) : (
          <InputGroup>
            <label>Enter your mnemonic words</label>
            <TextArea
              value={mnemonic}
              onChange={(e) => {
                setMnemonic(e.target.value);
                setError('');
              }}
              placeholder="Enter your 12, 15, 18, 21, or 24 mnemonic words, separated by spaces"
              onClick={e => e.stopPropagation()}
            />
            <InfoText>
              Usually 12 or 24 words separated by spaces (e.g., "abandon ability able about above absent absorb abstract absurd abuse access accident").
            </InfoText>
          </InputGroup>
        )}
        
        {error && <ErrorMessage>{error}</ErrorMessage>}

        <ButtonGroup>
          <Button onClick={onClose}>
            Cancel
          </Button>
          <Button 
            primary 
            onClick={handleSubmit}
          >
            Import Wallet
          </Button>
        </ButtonGroup>
      </ModalContent>
    </ModalOverlay>
  );
}; 